/**
 * TemplateSelectionModal - Select which familiarisation template to use
 */
import React, { useState } from 'react';
import { X, FileText, Check, Star } from 'lucide-react';
import { getTemplateItemCount } from '../../../../utils/familiarisationTemplateHelpers';

const TemplateSelectionModal = ({
  isOpen,
  onClose,
  onSelect,
  templates,
  crewMember,
  recommendedTemplateId
}) => {
  const [selectedId, setSelectedId] = useState(recommendedTemplateId || templates[0]?.id);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const template = templates.find(t => t.id === selectedId);
    if (template) {
      onSelect(template);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Start Familiarisation
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {crewMember?.full_name || crewMember?.name} ({crewMember?.position})
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-4">
              Select which checklist template to use:
            </p>

            <div className="space-y-2">
              {templates.map(template => {
                const itemCount = getTemplateItemCount(template);
                const isSelected = selectedId === template.id;
                const isRecommended = template.id === recommendedTemplateId;

                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedId(template.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? 'border-cyan-500 bg-cyan-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className={`h-4 w-4 ${isSelected ? 'text-cyan-600' : 'text-gray-400'}`} />
                          <span className="font-medium text-gray-900">{template.name}</span>
                          {isRecommended && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full flex items-center gap-1">
                              <Star className="h-3 w-3" />
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {itemCount} items &bull; {template.sections?.length || 0} sections
                          {template.validityPeriodMonths === 0
                            ? ' \u2022 No expiry'
                            : ` \u2022 Valid for ${template.validityPeriodMonths} months`}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700"
            >
              Start Familiarisation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateSelectionModal;
